import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesomeRounded,
  AutorenewRounded,
  CloseRounded,
  DescriptionRounded,
  PictureAsPdfRounded,
  RefreshRounded,
  VisibilityRounded,
} from '@mui/icons-material';
import { Empty } from 'antd';
import {
  downloadGeneratedReportPdf,
  getGeneratedReportHtml,
  getGeneratedReports,
  getGeneratedReportVersions,
  getReportsV2,
  regenerateGeneratedReport,
} from '../../../../helpers/api';
import { reportStatuses, userCats } from '../../../../helpers/appstate';
import { getYears } from '../../../../helpers/dateHelpers';
import { openNotification } from '../../../../helpers/notification';
import { AppContext } from '../../../../store/AppContext';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import FacilityAiSkillReports from './FacilityAiSkillReports';
import ReportViewerDialog from './ReportViewerDialog';
import AnalyticalReport2 from '../../reporting/reports/AnalyticalReport2';

const POLL_INTERVAL_MS = 20000;
const IN_PROGRESS = ['QUEUED', 'PROCESSING'];

const reportTypeLabels = { QAQC: 'QA/QC', NORMAL: 'Consultant' };

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

const getPeriod = (report) => `Q${report.reportQuarter} ${report.reportYear}`;

const getPdfFileName = (report) =>
  `IEMS-${(report.facilityName || 'Facility').replace(/[^\w]+/g, '-')}-${getPeriod(report).replace(
    ' ',
    '-'
  )}-${report.reportType}-v${report.version}.pdf`;

const needsPolling = (report) =>
  IN_PROGRESS.includes(report.status) || IN_PROGRESS.includes(report.pendingStatus);

function StatusChip({ report }) {
  if (IN_PROGRESS.includes(report.status)) {
    return (
      <Chip
        size="small"
        color="info"
        variant="outlined"
        icon={<CircularProgress size={12} color="inherit" />}
        label="Generating…"
      />
    );
  }
  if (report.status === 'FAILED') {
    return (
      <Tooltip title={report.errorMessage || ''}>
        <Chip size="small" color="error" variant="outlined" label="Couldn't generate" />
      </Tooltip>
    );
  }
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Chip size="small" color="success" variant="outlined" label="Ready" />
      {IN_PROGRESS.includes(report.pendingStatus) && (
        <Chip
          size="small"
          variant="outlined"
          icon={<CircularProgress size={12} color="inherit" />}
          label="Updating…"
        />
      )}
      {report.pendingStatus === 'FAILED' && (
        <Tooltip title={report.pendingError || ''}>
          <Chip size="small" color="warning" variant="outlined" label="Update failed" />
        </Tooltip>
      )}
    </Stack>
  );
}

export default function FacilityGeneratedReports({ facilityId }) {
  const { state } = useContext(AppContext);
  const userCategory = state?.auth?.result?.data?.userCategory;
  const userPermission = state?.auth?.result?.data?.userPermission || [];
  const canRegenerate = userPermission
    .flatMap((p) => p.permissionActivity || [])
    .some((a) => a.privilegeCode?.toLowerCase() === 'record_report');

  const [reports, setReports] = useState([]);
  const [publishedReports, setPublishedReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [yearFilter, setYearFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [busy, setBusy] = useState({}); // { [key]: true } for per-row spinners
  const [view, setView] = useState('standard'); // 'standard' | 'ai'
  const [refreshKey, setRefreshKey] = useState(0);
  const [accessDenied, setAccessDenied] = useState(false);
  // Report opened from the fallback list (users the generated-reports API refuses, e.g. personnel)
  const [fallbackReport, setFallbackReport] = useState(null);
  const pollRef = useRef(null);

  // Viewer state
  const [viewing, setViewing] = useState(null); // the list row being viewed
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [html, setHtml] = useState('');
  const [htmlLoading, setHtmlLoading] = useState(false);
  const [htmlError, setHtmlError] = useState('');

  const setBusyKey = (key, value) => setBusy((prev) => ({ ...prev, [key]: value }));

  const fetchReports = useCallback(
    async ({ silent = false } = {}) => {
      if (!facilityId) return;
      if (!silent) setLoading(true);
      try {
        const [generatedRes, publishedRes] = await Promise.allSettled([
          getGeneratedReports({ facilityId, year: yearFilter, reportType: typeFilter }),
          // Published monitoring reports, to offer generation for any that have no generated report yet
          getReportsV2('', facilityId, '', reportStatuses.PUBLISHED, yearFilter, typeFilter, '', '', 1, 200),
        ]);

        if (generatedRes.status === 'fulfilled') {
          setReports(generatedRes.value.data);
          setAccessDenied(false);
        } else if (generatedRes.reason?.accessDenied) {
          // No permission: show a notice in the tab instead of an error toast
          setReports([]);
          setAccessDenied(true);
        } else if (!silent) {
          openNotification({
            type: 'error',
            message: generatedRes.reason?.message || 'Unable to load reports',
          });
        }

        if (publishedRes.status === 'fulfilled') {
          const rows = publishedRes.value?.result?.data;
          setPublishedReports(
            Array.isArray(rows)
              ? rows.filter((r) => r.reportId && r.reportStatus === reportStatuses.PUBLISHED)
              : []
          );
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [facilityId, yearFilter, typeFilter]
  );

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Poll while any report (or a newer version of it) is still being generated
  useEffect(() => {
    clearTimeout(pollRef.current);
    if (reports.some(needsPolling)) {
      pollRef.current = setTimeout(() => fetchReports({ silent: true }), POLL_INTERVAL_MS);
    }
    return () => clearTimeout(pollRef.current);
  }, [reports, fetchReports]);

  const loadHtml = async (id) => {
    setHtml('');
    setHtmlError('');
    setHtmlLoading(true);
    try {
      setHtml(await getGeneratedReportHtml(id));
    } catch (error) {
      // 409 = still being generated; the server message explains it
      setHtmlError(error.message);
    } finally {
      setHtmlLoading(false);
    }
  };

  const openViewer = async (report) => {
    setViewing(report);
    setSelectedVersionId(report.id);
    setVersions([]);
    loadHtml(report.id);
    try {
      setVersions(await getGeneratedReportVersions(report.reportId));
    } catch (error) {
      // Version list is optional; the latest ready version is already shown
    }
  };

  const closeViewer = () => {
    setViewing(null);
    setHtml('');
    setHtmlError('');
  };

  const handleVersionChange = (event) => {
    setSelectedVersionId(event.target.value);
    loadHtml(event.target.value);
  };

  const handleDownload = async (report) => {
    const key = `pdf-${report.id}`;
    setBusyKey(key, true);
    try {
      await downloadGeneratedReportPdf(report.id, getPdfFileName(report));
    } catch (error) {
      openNotification({ type: 'error', message: error.message });
    } finally {
      setBusyKey(key, false);
    }
  };

  const handleRegenerate = async (report) => {
    const key = `regen-${report.reportId}`;
    setBusyKey(key, true);
    try {
      await regenerateGeneratedReport(report.reportId);
      openNotification({
        type: 'success',
        message:
          report.reportQuarter && report.reportYear
            ? `The ${getPeriod(report)} report is being generated. Any current version stays available until it's ready.`
            : "The report is being generated. Any current version stays available until it's ready.",
      });
      await fetchReports({ silent: true });
    } catch (error) {
      openNotification({ type: 'error', message: error.message });
    } finally {
      setBusyKey(key, false);
    }
  };

  // Published monitoring reports that have never been generated (e.g. published before this feature)
  const ungeneratedReports = publishedReports.filter(
    (p) => !reports.some((r) => String(r.reportId) === String(p.reportId))
  );

  // Published reports an AI report can be requested for (published list plus any already generated)
  const aiReportOptions = [...publishedReports, ...reports].filter(
    (r, i, all) => r.reportId && all.findIndex((x) => String(x.reportId) === String(r.reportId)) === i
  );

  const handleRefresh = () => {
    fetchReports();
    setRefreshKey((k) => k + 1);
  };

  const selectedVersion =
    versions.find((v) => v.id === selectedVersionId) ||
    (viewing && viewing.id === selectedVersionId ? viewing : null);

  const renderActions = (report) => {
    const isReady = report.status === 'READY';
    const regenBusy = busy[`regen-${report.reportId}`];
    const regenDisabled = regenBusy || IN_PROGRESS.includes(report.pendingStatus) || IN_PROGRESS.includes(report.status);

    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {isReady && (
          <>
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<VisibilityRounded />}
              onClick={() => openViewer(report)}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
            >
              View
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                busy[`pdf-${report.id}`] ? <CircularProgress size={14} /> : <PictureAsPdfRounded />
              }
              disabled={busy[`pdf-${report.id}`]}
              onClick={() => handleDownload(report)}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
            >
              {busy[`pdf-${report.id}`] ? 'Preparing PDF…' : 'Download PDF'}
            </Button>
          </>
        )}
        {canRegenerate && !IN_PROGRESS.includes(report.status) &&  userCategory === userCats.EDM && (
          <Tooltip title="Build a new version with the latest data and recommendations">
            <span>
              <Button
                size="small"
                color="inherit"
                startIcon={regenBusy ? <CircularProgress size={14} /> : <AutorenewRounded />}
                disabled={regenDisabled}
                onClick={() => handleRegenerate(report)}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', color: 'text.secondary' }}
              >
                Regenerate
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
    );
  };

  const renderFallbackList = () => {
    if (!publishedReports.length) {
      return (
        <Box sx={{ py: 10, textAlign: 'center', bgcolor: alpha('#f4f6f8', 0.5), borderRadius: '16px' }}>
          <Empty description="No published reports yet. Reports appear here after a quarterly report is published." />
        </Box>
      );
    }

    return (
      <Stack spacing={2}>
        {publishedReports.map((published) => {
          const hasPeriod = published.reportQuarter && published.reportYear;
          return (
            <Card
              key={`fallback-${published.reportId}`}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                '&:hover': { borderColor: 'primary.light' },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: alpha('#0c5bab', 0.08),
                    color: 'primary.main',
                    flexShrink: 0,
                  }}
                >
                  <DescriptionRounded />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {hasPeriod
                        ? `Q${published.reportQuarter} ${published.reportYear} Report`
                        : published.reportTitle || 'Published report'}
                    </Typography>
                    {published.reportType && (
                      <Chip
                        size="small"
                        label={reportTypeLabels[published.reportType] || published.reportType}
                        sx={{ fontWeight: 600, height: 22 }}
                      />
                    )}
                  </Stack>
                  <Chip size="small" color="success" variant="outlined" label="Published" sx={{ mt: 0.75 }} />
                </Box>
              </Stack>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<VisibilityRounded />}
                disabled={!hasPeriod}
                onClick={() => setFallbackReport(published)}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
              >
                View report
              </Button>
            </Card>
          );
        })}
      </Stack>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Stack spacing={2}>
          {[1, 2, 3].map((n) => (
            <LoaderSkeleton key={n} height={96} />
          ))}
        </Stack>
      );
    }

    // The generated-reports API refused (e.g. personnel without a role): list the published
    // quarters instead and open each one with the analytical report, which they can access
    if (accessDenied) {
      return renderFallbackList();
    }

    if (!reports.length && !ungeneratedReports.length) {
      return (
        <Box sx={{ py: 10, textAlign: 'center', bgcolor: alpha('#f4f6f8', 0.5), borderRadius: '16px' }}>
          <Empty description="No generated reports yet. Reports appear here automatically after a quarterly report is published." />
        </Box>
      );
    }

    return (
      <Stack spacing={2}>
        {ungeneratedReports.map((published) => {
          const regenBusy = busy[`regen-${published.reportId}`];
          const period =
            published.reportQuarter && published.reportYear
              ? `Q${published.reportQuarter} ${published.reportYear} Report`
              : published.reportTitle || 'Published report';
          return (
            <Card
              key={`published-${published.reportId}`}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '16px',
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: alpha('#f4f6f8', 0.6),
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: alpha('#8a94a6', 0.12),
                    color: 'text.secondary',
                    flexShrink: 0,
                  }}
                >
                  <DescriptionRounded />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {period}
                    </Typography>
                    {published.reportType && (
                      <Chip
                        size="small"
                        label={reportTypeLabels[published.reportType] || published.reportType}
                        sx={{ fontWeight: 600, height: 22 }}
                      />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.75 }}>
                    <Chip size="small" variant="outlined" label="Not generated" />
                    <Typography variant="caption" color="text.secondary">
                      Published, but no report has been generated yet
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
              {canRegenerate && (
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={
                    regenBusy ? <CircularProgress size={14} color="inherit" /> : <AutorenewRounded />
                  }
                  disabled={regenBusy}
                  onClick={() =>
                    handleRegenerate({
                      reportId: published.reportId,
                      reportQuarter: published.reportQuarter,
                      reportYear: published.reportYear,
                    })
                  }
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
                >
                  Generate report
                </Button>
              )}
            </Card>
          );
        })}
        {reports.map((report) => (
          <Card
            key={report.id}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '16px',
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              transition: 'border-color 0.2s',
              '&:hover': { borderColor: 'primary.light' },
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha('#0c5bab', 0.08),
                  color: 'primary.main',
                  flexShrink: 0,
                }}
              >
                <DescriptionRounded />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {getPeriod(report)} Report
                  </Typography>
                  <Chip
                    size="small"
                    label={reportTypeLabels[report.reportType] || report.reportType}
                    sx={{ fontWeight: 600, height: 22 }}
                  />
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    v{report.version}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                  <StatusChip report={report} />
                  <Typography variant="caption" color="text.secondary">
                    {report.completedAt
                      ? `Generated ${formatDate(report.completedAt)}`
                      : `Queued ${formatDate(report.queuedAt)}`}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
            {renderActions(report)}
          </Card>
        ))}
      </Stack>
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Facility Reports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generated when quarterly results are published.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Shown to everyone: AI reports are open to anyone who can access the facility,
              even when the standard reports list is refused (e.g. personnel) */}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, value) => value && setView(value)}
            sx={{
              gap: 1,
              flexShrink: 0,
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 700,
                px: 1.5,
                gap: 0.75,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              },
              // Separate the buttons instead of the default joined group
              '& .MuiToggleButtonGroup-grouped': {
                m: 0,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px !important',
              },
            }}
          >
            <ToggleButton value="standard">
              <DescriptionRounded sx={{ fontSize: 18 }} />
              Standard
            </ToggleButton>
            <ToggleButton value="ai">
              <AutoAwesomeRounded sx={{ fontSize: 18 }} />
              AI Report
            </ToggleButton>
          </ToggleButtonGroup>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Year</InputLabel>
            <Select label="Year" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {getYears()
                .slice()
                .reverse()
                .map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {/* <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Type</InputLabel>
            <Select label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="NORMAL">Consultant</MenuItem>
              <MenuItem value="QAQC">QA/QC</MenuItem>
            </Select>
          </FormControl> */}
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {view === 'ai' ? (
        <FacilityAiSkillReports
          facilityId={facilityId}
          yearFilter={yearFilter}
          reportOptions={aiReportOptions}
          refreshKey={refreshKey}
        />
      ) : (
        renderContent()
      )}

      {/* Fallback report viewer (analytical report, includes Download PDF) */}
      <Dialog
        open={Boolean(fallbackReport)}
        onClose={() => setFallbackReport(null)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: { borderRadius: '16px', height: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
        }}
      >
        {fallbackReport && (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 3, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Q{fallbackReport.reportQuarter} {fallbackReport.reportYear} Report
                {fallbackReport.reportType
                  ? ` (${reportTypeLabels[fallbackReport.reportType] || fallbackReport.reportType})`
                  : ''}
              </Typography>
              <IconButton onClick={() => setFallbackReport(null)} aria-label="Close report">
                <CloseRounded />
              </IconButton>
            </Stack>
            <Box sx={{ flex: 1, minHeight: 0 }}>
            <AnalyticalReport2
              embedded
              props={{ userId: state?.auth?.result?.data?.userId, userCategory }}
              preset={{
                facilityId,
                facilityName: fallbackReport.facilityName || fallbackReport.facility || '',
                year: fallbackReport.reportYear,
                quarter: fallbackReport.reportQuarter,
              }}
            />
            </Box>
          </>
        )}
      </Dialog>

      {/* Report viewer */}
      <ReportViewerDialog
        open={Boolean(viewing)}
        onClose={closeViewer}
        title={
          viewing
            ? `${viewing.facilityName} — ${getPeriod(viewing)} (${
                reportTypeLabels[viewing.reportType] || viewing.reportType
              })`
            : ''
        }
        subtitle={selectedVersion?.completedAt ? `Generated ${formatDate(selectedVersion.completedAt)}` : ''}
        headerExtra={
          viewing && versions.filter((v) => v.status === 'READY').length > 1 ? (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Version</InputLabel>
              <Select label="Version" value={selectedVersionId} onChange={handleVersionChange}>
                {versions
                  .filter((v) => v.status === 'READY')
                  .map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      v{v.version}
                      {v.id === viewing.id ? ' (latest)' : ''}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          ) : null
        }
        html={html}
        loading={htmlLoading}
        error={htmlError}
        onRetry={() => loadHtml(selectedVersionId)}
        onDownload={() => handleDownload(selectedVersion)}
        downloading={busy[`pdf-${selectedVersionId}`]}
        downloadDisabled={!selectedVersion}
      />
    </Box>
  );
}
